import { useEffect, useState } from "react";
import { Gavel } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useSiteConfig } from "../context/ConfigContext";
import { AuctionCard, aucId } from "../components/AuctionSection";

const TABS = [
  { key: "live", t: "auc_live" },
  { key: "scheduled", t: "auc_soon" },
  { key: "ended", t: "auc_ended" },
];

export default function Auctions() {
  const { user } = useAuth();
  const { t } = useLang();
  const { auction } = useSiteConfig();
  const [tab, setTab] = useState("live");
  const [items, setItems] = useState(null);
  const [mine, setMine] = useState(null);

  useEffect(() => {
    if (tab === "mine") {
      if (!user) { setMine([]); return; }
      setMine(null);
      api("/me/auctions", { auth: true }).then((r) => setMine(Array.isArray(r) ? r : r.items || [])).catch(() => setMine([]));
      return;
    }
    setItems(null);
    api(`/auctions?status=${tab}`).then((r) => setItems(r.items || [])).catch(() => setItems([]));
  }, [tab, user]);

  if (auction && auction.enabled === false) {
    return (
      <div data-testid="auctions-page" className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
        <p className="font-heading text-lg font-bold text-slate-800">{t("auc_off")}</p>
      </div>
    );
  }

  const list = tab === "mine" ? mine : items;

  return (
    <div data-testid="auctions-page" className="mx-auto max-w-5xl px-4 pb-16 pt-5 sm:px-6">
      <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold tracking-tight text-slate-900">
        <Gavel className="h-6 w-6 text-orange-600" /> {t("nav_auctions")}
      </h1>

      <div className="mt-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            data-testid={`auction-tab-${tb.key}`}
            onClick={() => setTab(tb.key)}
            className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-bold transition active:scale-95 ${
              tab === tb.key ? "bg-teal-700 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-teal-400"
            }`}
          >
            {t(tb.t)}
          </button>
        ))}
        {user && (
          <button
            data-testid="auction-tab-mine"
            onClick={() => setTab("mine")}
            className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-bold transition active:scale-95 ${
              tab === "mine" ? "bg-teal-700 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-teal-400"
            }`}
          >
            {t("my_bids")}
          </button>
        )}
      </div>

      {list === null ? (
        <p data-testid="auctions-loading" className="py-12 text-center text-sm text-slate-500">...</p>
      ) : list.length === 0 ? (
        <div data-testid="auctions-empty" className="mt-6 rounded-2xl border border-dashed border-slate-300 py-14 text-center">
          <Gavel className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 font-heading font-semibold text-slate-800">{t("no_auctions")}</p>
          <p className="mt-1 text-sm text-slate-500">{t("no_auctions_sub")}</p>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {list.map((a) => (
            <div key={aucId(a)} className="[&>a]:w-full">
              <AuctionCard a={a} tid={`auction-list-${aucId(a)}`} />
              {tab === "mine" && (a.my_status || a.result) && (
                <span className="mt-1 inline-block rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold capitalize text-teal-800">
                  {t(`bid_${a.my_status || a.result}`)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
